import { useLocation, useParams } from '@solidjs/router';
import { createEffect, createMemo, Match, on, ResourceReturn, Show, Switch, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { HCPWarning } from '../hcp-warning/hcp-warning';
import DynamicModuleGenerator from '../../dynamic-module-generator';
import { Grid } from '../../grid-system/grid/grid';
import { Section } from '../../grid-system/section/section';
import theme from '../../style/theme';
import { PageCacheType } from '../../types/shared';
import { Heading } from '../../ui-components/heading/heading';
import { LockedContent } from '../../ui-components/layouts/locked-content';
import { Link } from '../../ui-components/link/link';
import { StyledRelativeContainer } from '../../ui-components/utility-style-components/positioning';
import { NotFound } from '../404/404';
import { DocumentTypeFlag } from '../document-type-flag/document-type-flag';
import { CalendarIcon } from '../icons-library/calendar';
import { CompassIcon } from '../icons-library/compass';
import { RelatedPostsList } from '../related-posts-list/related-posts-list';
import { SeoSettings } from '../seo-settings/seo-settings';
import { ShareAndSave } from '../share-and-save/share-and-save';
import {
    StyledAuthor,
    StyledBreadCrumbsWrapper,
    StyledCurrentPage,
    StyledDate,
    StyledEvent,
    StyledEventHeader,
    StyledEventOuter,
    StyledEventWrapper,
    StyledHeaderContentLeft,
    StyledHeaderContentRight,
    StyledLink,
    StyledLocation,
    StyledText,
    StyledEventCardContainer
} from './event.styles';
import { fetchRelatedEvents, GET_EVENT_QUERY } from './fetch-event';
import { formatDateDigits } from '../../tools/date-format';
import removeAmpersand from '../../tools/remove-ampersand';
import { EventContextProvider } from './event-context-provider';
import { EventData } from './event-types';
import { isOnDemandEvent } from '../events/helpers/is-on-demand-event';
import { navigateToAnchor } from '../../tools/scroll-to-element';

type EventContent = ResourceReturn<{
    event?: {
        content?: EventData;
    }
}>;

export const Event = () => {
    const location = useLocation();
    const params = useParams<{ eventSlug: string }>();

    const { 
        appStore,
        setCacheType,
        setStatusCode,
        localize,
        eventsListingPage,
        hcpEventsListingPage,
        viewport,
        userState,
        pageRegistration,
        createCachedResource,
        siteInfo,
        headerSettings,
    } = useContext(AppContext);

    const isTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;
    const isMobile = () => viewport.width <= theme.breakpoints.MOBILE;
    const isOriginalTablet = () => viewport.width <= theme.breakpoints.ORIGINAL_TABLET;

    const showHcpHeader = () => headerSettings.activateHcpHeader && isHcpEvent() && !isOriginalTablet();

    const eventData: EventContent = createCachedResource(GET_EVENT_QUERY, () => ({ key: `/event/${params.eventSlug}` }), true);
    const [ eventContent ] = eventData;

    const tags = () => eventContent()?.event?.content?.tags;
    const currentEventId = () => eventContent()?.event?.content?.id;
    const relatedEventsData = createMemo(() => fetchRelatedEvents(tags(), currentEventId()));        

    createEffect(
        on(
            () => location.hash,
            () => navigateToAnchor({
                id: location.hash.replace('#', ''),
                smallScreen: isMobile(),
                isCareSite: siteInfo.siteType === 'atos-care',
            })
        )
    );

    const isHcpEvent = () => {
        if (checkHcp()) {
            setCacheType(PageCacheType.none);
            return true;
        }
        setCacheType(PageCacheType.defaultCache);
        return false;
    };

    const checkHcp = () => {
        /**
         * "appStore.isHcp" behaves like a ratchet, once set to true, it can't be set to false,
         * unless the homepage is visited (which is a "patients" page, not a HCP page)
         */
        if (location.pathname === '/') {
            return false;
        }

        if (appStore.isHcp()) {
            return true;
        }
        
        return Boolean(eventContent()?.event?.content?.isHealthcareProfessional);
    };

    const setHcp = () => {
        const isHcp = checkHcp();
        appStore.setHcp(isHcp);
        return null;
    };

    setHcp();

    createEffect(() => {        
        setHcp();
    });

    const isHiddenForPublic = () => {
        return eventContent()?.event?.content?.isHealthcareProfessional
            && !eventContent()?.event?.content?.isVisibleForPublic 
            && !userState.user
            && pageRegistration.url !== '';
    };

    const getEventDate = () => {
        const eventDate = eventContent()?.event?.content?.eventDate;

        if (isOnDemandEvent(eventContent()?.event?.content)) {
            return localize('on-demand', 'On demand');
        } else if (eventDate) {
            return formatDateDigits(eventDate, siteInfo.key);
        }
        return '';
    };

    const checkNotFound = createMemo(() => {
        if (!eventContent()?.event?.content) {
            // This only exists on SSR. We call it to set the right status from server.
            if (typeof setStatusCode === 'function') {
                setStatusCode(404);
            }
            return true;
        } else {
            if (typeof setStatusCode === 'function') {
                setStatusCode(200);
            }
            return false;
        }
    });

    const eventsSectionTemplate = () => {
        const template = eventContent()?.event?.content?.showRelatedEvents && eventContent()?.event?.content?.relatedEventsPosition === 'sidebar' && !isTablet() ? [8, 4] : [12];
        return template;
    };

    const isActiveEvent = () => {
        /**
         * eventExpiryDate takes precedence over eventDate - one is for on-demand events, 
         * the other for live events, but nothing prevents editors from setting both
         */
        const eventDate = eventContent()?.event?.content?.eventExpireDate || eventContent()?.event?.content?.eventDate;
        if (!eventDate) return true;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const eventDateObj = new Date(eventDate);
        eventDateObj.setHours(0, 0, 0, 0);

        return eventDateObj >= today;
    };

    const showRegistrationLink = () => {
        if (eventContent()?.event?.content?.isFullyBooked) return false;
        if (!isActiveEvent()) return false;

        return eventContent()?.event?.content?.eventRegistrationLink?.url;
    };

    return (
        <div>
            <StyledEventWrapper adjustForHcpHeader={showHcpHeader()}>
                <Show when={checkNotFound() && !eventContent.loading}>
                    <NotFound />
                </Show>
                {/* Don't even try a fallback LoadingSpinner here, because it breaks SSR and stuff */}
                <Show when={!checkNotFound() && !eventContent.loading}>
                    <HCPWarning isHcp={isHcpEvent()} />
                    <SeoSettings seo={eventContent()?.event?.content?.seo} title={eventContent()?.event?.content?.post_title} />

                    <StyledEventCardContainer>
                        
                        <Section widthType="bgFull" templateShorthand={eventsSectionTemplate() || [ 8, 4 ]}>
                            <StyledEventOuter
                                fullWidth={
                                    !eventContent()?.event?.content?.showRelatedEvents ||
                                    eventContent()?.event?.content?.relatedEventsPosition === 'bottom' ||
                                    isTablet()
                                }
                            >
                                <Show when={isMobile()}>
                                    <Grid templateShorthand={[ 12 ]} isLayoutGrid={true}>
                                        <StyledBreadCrumbsWrapper>
                                            <Switch>
                                                <Match when={isHcpEvent() && hcpEventsListingPage.url}>
                                                    <Link
                                                        url={hcpEventsListingPage.url}
                                                        label={hcpEventsListingPage.title}
                                                        plainStyle={true}
                                                        arrowDirection={'left'}
                                                        arrowPosition='left'
                                                        size={'small'}
                                                    />
                                                </Match>
                                                <Match when={!isHcpEvent() && eventsListingPage.url}>
                                                    <Link
                                                        url={eventsListingPage.url}
                                                        label={eventsListingPage.title}
                                                        plainStyle={true}
                                                        arrowDirection={'left'}
                                                        arrowPosition='left'
                                                        size={'small'}
                                                    />
                                                </Match>
                                            </Switch>

                                        </StyledBreadCrumbsWrapper>
                                    </Grid>
                                </Show>


                                <Show when={!isMobile()}>
                                    <StyledBreadCrumbsWrapper>
                                        <Switch>
                                            <Match when={isHcpEvent() && hcpEventsListingPage.url}>
                                                <StyledLink href={hcpEventsListingPage.url}>{hcpEventsListingPage.title}</StyledLink>
                                                <span> /</span>
                                                <StyledCurrentPage>{removeAmpersand(eventContent()?.event?.content?.post_title)}</StyledCurrentPage>
                                            </Match>
                                            <Match when={!isHcpEvent() && eventsListingPage.url}>
                                                <StyledLink href={eventsListingPage.url}>{eventsListingPage.title}</StyledLink>
                                                <span> /</span>
                                                <StyledCurrentPage>{removeAmpersand(eventContent()?.event?.content?.post_title)}</StyledCurrentPage>
                                            </Match>
                                        </Switch>
                                    </StyledBreadCrumbsWrapper>
                                </Show>
                                <StyledEvent>
                                    <Show when={isHiddenForPublic()}>
                                        <LockedContent />
                                    </Show>
                                    <StyledEventHeader>
                                        <StyledHeaderContentLeft>
                                            <Heading tag={'h1'}>{eventContent()?.event?.content?.post_title}</Heading>
                                            <Show when={eventContent()?.event?.content?.listCardContent.displayHostName === 'yes'}>
                                                <StyledAuthor>
                                                    {localize('hosted-by', 'Hosted By')}: {eventContent()?.event?.content?.listCardContent.hostName}
                                                </StyledAuthor>
                                            </Show>
                                        </StyledHeaderContentLeft>

                                        <StyledHeaderContentRight>
                                            <Show when={isMobile() && eventContent()?.event?.content?.isFullyBooked}>
                                                <StyledText>{localize('event-fully-booked', 'Fully Booked')}</StyledText>
                                            </Show>
                                            <StyledDate>
                                                <StyledRelativeContainer top={-5}>
                                                    <CalendarIcon height={2.21} width={1.79} />
                                                </StyledRelativeContainer>
                                                <StyledText>
                                                    {getEventDate()}
                                                    
                                                </StyledText>
                                            </StyledDate>
                                            <Show when={eventContent()?.event?.content?.eventLocation}>
                                                <StyledLocation>
                                                    <StyledRelativeContainer top={-2}>
                                                        <CompassIcon height={2.21} width={1.79} />
                                                    </StyledRelativeContainer>
                                                    <StyledText>{eventContent()?.event?.content?.eventLocation}</StyledText>
                                                </StyledLocation>
                                            </Show>
                                            <Show when={showRegistrationLink()}>
                                                <Link
                                                    url={eventContent()!.event!.content!.eventRegistrationLink.url}
                                                    opensInNewTab={eventContent()!.event!.content!.eventRegistrationLink.target === '_blank'}
                                                    label={localize('register-now', 'Register now')}
                                                    arrowDirection={'right'}
                                                    arrowPosition='right'
                                                    arrowColor={theme.palette.red}
                                                    plainStyle={true}
                                                    labelColor={theme.palette.red}
                                                    size={'large'}
                                                />
                                            </Show>

                                            <Show when={!isMobile()}>
                                                <DocumentTypeFlag
                                                    type="event"
                                                    size="small"
                                                    customText={
                                                        eventContent()?.event?.content?.isFullyBooked ? localize('event-fully-booked', 'Fully Booked') : undefined
                                                    }
                                                />
                                            </Show>
                                        </StyledHeaderContentRight>
                                    </StyledEventHeader>

                                    <EventContextProvider 
                                        gotoEventId={eventContent()?.event?.content?.gotoEventId}
                                        isDisabledEvent={!isActiveEvent()}
                                    >
                                        <DynamicModuleGenerator
                                            content={eventContent()?.event?.content?.blocks}
                                            permalink={eventContent()?.event?.content?.permalink}
                                            pageTitle={eventContent()?.event?.content?.post_title}
                                        />
                                    </EventContextProvider>
                                    <ShareAndSave
                                        type="event"
                                        permalink={eventContent()?.event?.content?.permalink}
                                        title={eventContent()?.event?.content?.post_title}
                                        data={eventContent()?.event?.content}
                                    />
                                </StyledEvent>
                            </StyledEventOuter>
                            <Show
                                when={
                                    eventContent()?.event?.content?.showRelatedEvents &&
                                    eventContent()?.event?.content?.relatedEventsPosition === 'sidebar' &&
                                    relatedEventsData()?.length > 0
                                }
                            >
                                <RelatedPostsList
                                    heading={localize('related-events', 'Related events')}
                                    // TODO: as for now it is always vertical, but there is an option on events to show them horizontally.. look into that
                                    orientation="vertical"
                                    relatedPosts={relatedEventsData()}
                                />
                            </Show>
                        </Section>
                    </StyledEventCardContainer>
                    <Show
                        when={
                            eventContent()?.event?.content?.showRelatedEvents &&
                            eventContent()?.event?.content?.relatedEventsPosition === 'bottom' &&
                            relatedEventsData()?.length > 0
                        }
                    >
                        <Section widthType="bgFull" templateShorthand={[12]} isLayoutSection={true}>
                            <RelatedPostsList
                                heading={localize('related-events', 'Related events')}
                                // TODO: this should be horizontal when isue above is solved
                                orientation="horizontal"
                                relatedPosts={relatedEventsData()}
                            />
                        </Section>
                        {/* <Grid templateShorthand={[12]} isLayoutGrid={true}>
                            <Slider slidesPerView={1} slideHeight="auto" spacing={theme.grid.standardUnit}>
                                <For each={relatedEventsData()}>
                                    {(post: EventData) => {
                                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                        // @ts-ignore
                                        // TODO: fix types!
                                        const postContent = { ...post.content, permalink: '/' + post.content.url };
                                        return <PostCard data={postContent} />;
                                    }}
                                </For>
                            </Slider>
                        </Grid> */}
                    </Show>
                </Show>
            </StyledEventWrapper>
        </div>
    );
};
